import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { movies } from "@/mocks/movies";

const featured = movies.filter((movie) => ["6", "16", "1", "9", "4"].includes(movie.id));

export default function SpotlightCarousel() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  const next = useCallback(() => {
    setActive((prev) => (prev + 1) % featured.length);
  }, []);

  const prev = () => {
    setActive((prev) => (prev - 1 + featured.length) % featured.length);
  };

  useEffect(() => {
    if (paused) return;
    const timer = window.setInterval(next, 7000);
    return () => window.clearInterval(timer);
  }, [next, paused]);

  const movie = featured[active];

  return (
    <section className="relative w-full border-b border-background-200/60 bg-background-100/40">
      <div className="mx-auto w-full max-w-[1360px] px-4 py-12 md:px-8 md:py-16">
        <div className="mb-7 flex items-end justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="h-3 w-[3px] rounded-full bg-accent-500"></span>
              <span className="font-label text-[11px] uppercase tracking-[0.28em] text-accent-500">
                Spotlight
              </span>
            </div>
            <h2 className="mt-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              焦点推荐
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={prev}
              aria-label="上一部"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:border-accent-500 hover:text-accent-400"
            >
              <i className="ri-arrow-left-s-line text-lg"></i>
            </button>
            <button
              type="button"
              onClick={next}
              aria-label="下一部"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:border-accent-500 hover:text-accent-400"
            >
              <i className="ri-arrow-right-s-line text-lg"></i>
            </button>
          </div>
        </div>

        <div
          className="grid grid-cols-1 gap-6 rounded-lg border border-background-200/80 bg-background-50/70 p-4 md:gap-8 md:p-6 lg:grid-cols-12 lg:p-8"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div className="lg:col-span-4">
            <Link to={`/detail/${movie.id}`} className="group relative block overflow-hidden rounded-lg">
              <div className="aspect-[2/3] w-full overflow-hidden bg-background-200">
                <img
                  key={movie.id}
                  src={movie.poster}
                  alt={`${movie.title} 电影海报`}
                  title={`${movie.title} 详细剧情解析`}
                  className="h-full w-full animate-fade-in object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]"
                />
              </div>
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-background-50 via-background-50/60 to-transparent p-4 pt-10">
                <div className="flex items-center justify-between">
                  <span className="rounded bg-primary-500 px-2 py-1 font-label text-[11px] font-semibold tracking-wider text-background-50">
                    EDITOR'S PICK
                  </span>
                  <span className="flex items-center gap-1 text-sm">
                    <i className="ri-star-fill text-accent-500"></i>
                    <span className="font-label font-semibold text-foreground-950">
                      {movie.rating.toFixed(1)}
                    </span>
                  </span>
                </div>
              </div>
            </Link>
          </div>

          <div className="flex flex-col justify-center lg:col-span-8">
            <div className="flex items-baseline gap-4">
              <span className="font-display text-5xl font-semibold leading-none text-background-300">
                {String(active + 1).padStart(2, "0")}
              </span>
              <span className="font-label text-xs uppercase tracking-[0.24em] text-foreground-500">
                {movie.originalTitle}
              </span>
            </div>

            <h3 className="mt-3 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
              <Link to={`/detail/${movie.id}`} className="transition-colors hover:text-accent-400">
                {movie.title}
              </Link>
            </h3>

            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
              <span className="rounded bg-secondary-100 px-2 py-1 text-secondary-900">{movie.year}</span>
              {movie.genres.map((genre) => (
                <span key={genre} className="flex items-center gap-1">
                  <i className="ri-price-tag-3-line text-[11px]"></i>
                  {genre}
                </span>
              ))}
              <span className="flex items-center gap-1">
                <i className="ri-time-line text-[11px]"></i>
                {movie.duration} 分钟
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-user-star-line text-[11px]"></i>
                导演 {movie.director}
              </span>
            </div>

            <p className="mt-5 line-clamp-4 text-sm leading-7 text-foreground-600 md:text-[15px]">
              {movie.synopsis}
            </p>

            <div className="mt-5 rounded-md border-l-2 border-accent-500 bg-background-100/70 px-4 py-3">
              <p className="line-clamp-2 text-xs leading-6 text-foreground-600 md:text-[13px]">
                <span className="mr-2 font-label uppercase tracking-widest text-accent-400">点评</span>
                {movie.analysis[0]}
              </p>
            </div>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${movie.id}`}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                阅读完整解析
                <i className="ri-arrow-right-line text-sm"></i>
              </Link>
              <Link
                to="/category?c=hot"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-accent-500 hover:text-accent-400"
              >
                更多热门作品
              </Link>
            </div>

            <div className="mt-7 flex items-center gap-3">
              {featured.map((item, index) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setActive(index)}
                  aria-label={`查看第 ${index + 1} 部推荐`}
                  className={`h-1 cursor-pointer rounded-full transition-all ${
                    index === active ? "w-10 bg-accent-500" : "w-5 bg-background-400 hover:bg-background-500"
                  }`}
                ></button>
              ))}
              <span className="ml-2 font-label text-xs tracking-widest text-foreground-500">
                {String(active + 1).padStart(2, "0")} / {String(featured.length).padStart(2, "0")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}